'use client';

import { type CSSProperties, type ReactNode, useRef } from 'react';

import {
  InfiniteAppendList,
  type InfiniteAppendLoadMore,
} from '@ps/ui/molecules/InfiniteAppendList';
import { useRailScroll } from '@ps/ui/molecules/RailScroll';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { TILE_CELL_WIDTH } from '../../../../my-store/components/CollectionRackTiles';
import { collectionButtonHeaderStyle } from '../functions/collectionButtonHeaderStyle';

/** Tiles sit flush to the rail's edges and top-align (their names run below). */
const RAIL_CLASSES = 'no-scrollbar flex items-start gap-2 overflow-x-auto';
const RAIL_STYLE: CSSProperties = { scrollSnapType: 'x mandatory' };

interface Props {
  /** Collection name — the section heading. */
  name: string;
  /** Resolved heading style (`CollectionButtonViewModel.titleStyle`). */
  titleStyle: CSSProperties;
  /** Resolved heading / chevron color (`…titleTextColor`). */
  titleTextColor: string;
  /** Divider under the header — the card's own hairline (`rackColors`). */
  dividerColor: string;
  /** The rail's first page of tiles — server-rendered by the host. */
  children: ReactNode;
  /** Bound server action returning the next page as an RSC payload. */
  loadMoreProducts: InfiniteAppendLoadMore;
  /** Whether product pages exist beyond `children`. */
  productsHasMore: boolean;
}

/**
 * The VISITOR product rack of a collection custom button: a bare section — no
 * card, no border, no Shop-All footer — whose header carries the collection name
 * with the scroll chevrons inline to its right, over a rail of tiles that name
 * themselves below the thumbnail. Matches what the creator sees in
 * `/my-popstore` (`CollectionButtonView` → `CollectionHeader`).
 *
 * Separate from the My Store tab's `CollectionRackView` — that's the bordered
 * card WITH the name/count/Shop-All footer, the store-browse presentation, and
 * it stays as-is. Per the shared-component rule, a path-specific presentation is
 * its own component rather than flags on the pure one.
 *
 * Only the chevrons hydrate: the tiles arrive as server-rendered `children`, so
 * the products and their PDP links stay crawlable.
 */
export function CollectionButtonRackView({
  name,
  titleStyle,
  titleTextColor,
  dividerColor,
  children,
  loadMoreProducts,
  productsHasMore,
}: Props) {
  const railRef = useRef<HTMLDivElement>(null);
  // The shared rail-scroll measurement every horizontal rail uses — re-measures
  // as pages append, so the → chevron reappears when more products load in.
  const { overflow, canScrollLeft, canScrollRight, scrollByDirection } =
    useRailScroll(railRef, 0);

  return (
    <div>
      <div
        className="flex items-center justify-between gap-2"
        style={collectionButtonHeaderStyle(dividerColor)}
      >
        <span className="block flex-1 truncate" style={titleStyle}>
          {name}
        </span>
        {overflow ? (
          <div className="flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={() => scrollByDirection(-1)}
              disabled={!canScrollLeft}
              aria-label="Previous products"
              className="flex items-center justify-center disabled:opacity-30"
            >
              <ChevronLeft color={titleTextColor} size={24} aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => scrollByDirection(1)}
              disabled={!canScrollRight}
              aria-label="Next products"
              className="flex items-center justify-center disabled:opacity-30"
            >
              <ChevronRight color={titleTextColor} size={24} aria-hidden />
            </button>
          </div>
        ) : null}
      </div>

      <InfiniteAppendList
        orientation="horizontal"
        className={RAIL_CLASSES}
        style={RAIL_STYLE}
        scrollRef={railRef}
        initialPage={1}
        initialHasMore={productsHasMore}
        loadMore={loadMoreProducts}
        // Loading cell mirrors an empty tile (same placeholder bg + portrait
        // shape); the heading color drives the contrasting spinner.
        indicatorClassName="rounded-lg bg-white/5"
        indicatorStyle={{
          width: TILE_CELL_WIDTH,
          aspectRatio: '103 / 148',
          color: titleTextColor,
        }}
      >
        {children}
      </InfiniteAppendList>
    </div>
  );
}
